import { PusherEvent } from '@mondaygpt/constants'
import { useCallback, useEffect, useState } from 'react'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'

/**
 * The set of providers the current business has been restricted from publishing
 * to (Meta code 25 — see `BusinessIntegrations.restrictedAt`). Powers the red
 * "Restricted" badge on post cards so a post targeting a restricted platform
 * reads as blocked before it ever tries to publish.
 *
 * Concurrent cards mounting together (a week grid, the Posts list) share a
 * single in-flight request; every instance re-syncs live on the
 * `IntegrationRestrictionChanged` Pusher event (set OR cleared) so badges
 * appear/disappear the moment the server flips the flag.
 */
let inflight: Promise<string[]> | null = null

function fetchRestrictedPlatforms(): Promise<string[]> {
  if (!inflight) {
    inflight = IntegrationsService.integrationControllerGetIntegrationsV1()
      .then(({ integrations }) =>
        integrations.filter(integration => integration.isRestricted).map(i => i.provider)
      )
      .catch(error => {
        console.error('useRestrictedPlatforms: failed to load integrations', error)
        return []
      })
      // Release the shared promise once settled so a later mount refetches
      // (e.g. after a business switch) instead of reusing a stale result.
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export function useRestrictedPlatforms(): string[] {
  const [restrictedPlatforms, setRestrictedPlatforms] = useState<string[]>([])

  const refetch = useCallback(async () => {
    setRestrictedPlatforms(await fetchRestrictedPlatforms())
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // Restriction set/cleared server-side (also fires on a successful publish that
  // lifts it) → re-sync so the badge appears or disappears without a reload.
  useListenToPusherEvent(PusherEvent.IntegrationRestrictionChanged, () => {
    void refetch()
  })

  return restrictedPlatforms
}
